import { logger } from "@jslib/shared/utils/logger";
import { getSafe } from "@jslib/shared/utils/getSafe";

import { getOptions } from "../../options/options";
import { type DocumentData, type DocUploadData, type NetworkErrorId } from "../types/types";
import ResponseTimeLogger from "../utils/responseTimeLogger/ResponseTimeLogger";

import { isSuccessiveCall } from "./NetworkDebugUtils";

const defaultHeaders = {
  Accept: "application/json",
  "Content-Type": "application/json",
};

const defaultUploadHeaders = {
  "Content-Type": "application/json",
};

export const fetchWithTimeout = async (
  url: string,
  requestInit: RequestInit = {},
  /** optional b/c sometimes it has already been called */
  checkSuccessive = true,
) => {
  const timeout = getOptions().httpRequestTimeout;

  let urlSearch;
  try {
    const urlObj = new URL(url);
    urlSearch = urlObj?.search || "";
  } catch (e) {
    logger.error(e, undefined, { url });
  }

  const errorAttributes = {
    url: url.replace(urlSearch, ""),
    queryStr: urlSearch?.search,
    method: requestInit.method,
  };

  if (checkSuccessive) {
    // report errors to offsite logs, if applicable
    isSuccessiveCall(url, requestInit, errorAttributes);
  }

  return new Promise<Response>((resolve, reject) => {
    const timeoutTimer = setTimeout(() => {
      const networkErrorId: NetworkErrorId = "requestTimeout";
      logger.error("Request timeout", undefined, {
        ...errorAttributes,
        timeoutMs: `${timeout}`,
      });

      reject(new Error(networkErrorId));
    }, timeout);

    fetch(url, requestInit)
      .then((resp) => {
        if (resp.type === "error" || resp.status <= 0) throw new Error();
        resolve(resp);
      })
      .catch((error) => {
        logger.error("Failed to fetch", error, errorAttributes);
        const networkErrorId: NetworkErrorId = "failedToFetch";

        reject(new Error(networkErrorId));
      })
      .finally(() => {
        clearTimeout(timeoutTimer);
      });
  });
};

const processResponse = async (
  response: Response,
  timeLog: ResponseTimeLogger,
): Promise<object> => {
  let responseData: Promise<object>;
  try {
    responseData = await response.json();
  } catch (error) {
    // TODO - fix this! the chain of fn's that call this expect `VerificationResponse | ErrorResponse`
    // NOT a bare object. The types are way off
    // Handle 204 response
    responseData = Promise.resolve({});
  } finally {
    timeLog.logNow();
    logger.info("Response received", responseData);
  }
  return responseData;
};

export const PostJson = async (
  url: string,
  body: object,
  headers: { [key: string]: string } = {},
): Promise<object> => {
  const timeLog = new ResponseTimeLogger(url);
  const response = await fetchWithTimeout(url, {
    headers: { ...defaultHeaders, ...headers },
    method: "POST",
    body: JSON.stringify(body),
  });
  return processResponse(response, timeLog);
};

export const DeleteJson = async (
  url: string,
  headers: { [key: string]: string } = {},
): Promise<object> => {
  const timeLog = new ResponseTimeLogger(url);
  const response = await fetchWithTimeout(url, {
    headers: { ...defaultHeaders, ...headers },
    method: "DELETE",
  });
  return processResponse(response, timeLog);
};

export const GetJson = async (
  url: string,
  headers: { [key: string]: string } = {},
): Promise<object> => {
  const timeLog = new ResponseTimeLogger(url);
  const thePromise = fetchWithTimeout(url, {
    headers: { ...defaultHeaders, ...headers },
    method: "GET",
  });
  return processResponse(await thePromise, timeLog);
};

export const GetResponse = async (
  url: string,
  headers: { [key: string]: string } = {},
): Promise<object> =>
  fetchWithTimeout(url, {
    headers: { ...defaultHeaders, ...headers },
    method: "GET",
  });

export const createFormData = (files: File[]) => {
  const formData = new FormData();
  files.forEach((file, index) => {
    if (file) {
      formData.append(`file${index}`, file);
    }
  });
  return formData;
};

export const createDocUploadData = (files: File[]): DocUploadData[] =>
  files
    .filter((file) => !!file)
    .map((file) => ({
      fileName: file.name,
      mimeType: file.type,
      fileSize: file.size,
    }));

export const PostFiles = async (
  url: string,
  files: File[],
  headers = defaultUploadHeaders,
): Promise<object> => {
  const timeLog = new ResponseTimeLogger(url);
  logger.info(`PostFiles posting to ${url}`, files);
  const docUploadData: string = JSON.stringify({
    files: createDocUploadData(files),
  });

  const docUploadRsp: Response = await fetch(url, {
    headers,
    method: "POST",
    body: docUploadData,
  });
  const docUploadRspData = await docUploadRsp.json();

  const documents = getSafe(() => docUploadRspData.documents);
  const submissionUrl = getSafe(() => docUploadRspData.submissionUrl);

  if (documents && submissionUrl) {
    await Promise.all(
      documents.map((document: DocumentData, index: number) =>
        fetch(document.uploadUrl, {
          method: "PUT",
          body: files[index],
        }),
      ),
    );

    const completeDocUploadRsp = await fetch(submissionUrl, {
      headers,
      method: "POST",
    });

    // Normal return flow
    return processResponse(completeDocUploadRsp, timeLog);
  }

  // return initial docUpload response if an error occured
  return docUploadRspData;
};
